import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { TimeAgo } from "components/TimeAgo/TimeAgo";
import { TimeAgoWithDateTimeFormatter } from "components/TimeAgo/TimeAgoWithDateTimeFormatter";
import { ResourceConfig } from "config/config";
import { normalizeNullableStringFields } from "config/resourceUtils";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import { Sync_Config } from "graphql/generated/graphql";
import identity from "./identity";
import resource from "./resource";
import { getUrlDTO, isEnabledField } from "./sharedFields";

const resourceName = "sync_config";

const config: ResourceConfig = {
    label: "Sync config",
    icon: IconCollection.sync,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "syncConfigDeprecationNotice" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            ${identity.graphql.resourceBody}
            is_enabled
            remote_url
            sync_url
            cacert
            verify_ssl
            remote_public_key
            last_sync_send_at
            last_sync_receive_at
            last_activity
            has_own_private_key
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            remote_url
            sync_url
            has_own_private_key
            last_sync_send_at
            last_sync_receive_at
            verify_ssl
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $is_enabled: Boolean,
            $remote_url: String,
            $cacert: String,
            $verify_ssl: Boolean,
            $own_private_key: String,
            $remote_public_key: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            is_enabled: $is_enabled,
            remote_url: $remote_url,
            cacert: $cacert,
            verify_ssl: $verify_ssl,
            own_private_key: $own_private_key,
            remote_public_key: $remote_public_key,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $is_enabled: Boolean,
            $remote_url: String,
            $cacert: String,
            $verify_ssl: Boolean,
            $own_private_key: String,
            $remote_public_key: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            is_enabled: $is_enabled,
            remote_url: $remote_url,
            cacert: $cacert,
            verify_ssl: $verify_ssl,
            own_private_key: $own_private_key,
            remote_public_key: $remote_public_key,
        `,
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Sync URL",
            key: "sync_url",
            shown: true,
            sortable: false,
            dto: (value: any) =>
                buildSimpleColumn(
                    getUrlDTO(value.sync_url),
                    setNewValue
                        ? () => setNewValue({ field: "sync_url", value: value.sync_url })
                        : undefined
                ),
        },
        {
            label: "Remote URL",
            key: "remote_url",
            shown: true,
            sortable: false,
            dto: (value: any) =>
                buildSimpleColumn(
                    getUrlDTO(value.remote_url),
                    setNewValue
                        ? () => setNewValue({ field: "remote_url", value: value.remote_url })
                        : undefined
                ),
        },
        {
            label: "Has own private key",
            key: "has_own_private_key",
            shown: true,
            sortable: false,
            dto: ({ has_own_private_key }: Sync_Config) =>
                has_own_private_key
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () =>
                                    setNewValue({
                                        field: "has_own_private_key",
                                        value: has_own_private_key,
                                    })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Last sync sent",
            key: "last_sync_send_at",
            shown: true,
            sortable: false,
            dto: ({ last_sync_send_at }: Sync_Config) =>
                last_sync_send_at
                    ? buildSimpleColumn(
                          <TimeAgo datetime={last_sync_send_at} />,
                          setNewValue
                              ? () =>
                                    setNewValue({
                                        field: "last_sync_send_at",
                                        value: last_sync_send_at,
                                    })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Last sync received",
            key: "last_sync_receive_at",
            shown: true,
            sortable: false,
            dto: ({ last_sync_receive_at }: Sync_Config) =>
                last_sync_receive_at
                    ? buildSimpleColumn(
                          <TimeAgo datetime={last_sync_receive_at} />,
                          setNewValue
                              ? () =>
                                    setNewValue({
                                        field: "last_sync_receive_at",
                                        value: last_sync_receive_at,
                                    })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Verify SSL",
            key: "verify_ssl",
            shown: true,
            sortable: true,
            dto: (value: Sync_Config) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="verify_ssl"
                        value={value.verify_ssl ?? undefined}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "verify_ssl",
                                  value: value.verify_ssl,
                              })
                        : undefined
                ),
        },
    ],
    form: [
        isEnabledField,
        {
            name: "remote_url",
            component: "url",
            fieldProps: {
                label: "Remote URL",
            },
        },
        {
            name: "sync_url",
            component: "url",
            fieldProps: {
                label: "Sync URL",
                asLink: true,
            },
        },
        {
            name: "cacert",
            component: "textarea",
            fieldProps: {
                label: "CA certificate",
            },
        },
        {
            name: "verify_ssl",
            component: "switch",
            fieldProps: {
                label: "Verify SSL certificates",
            },
        },
        {
            name: "own_private_key",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Own private key",
                clearable: false,
                isMultiLine: true,
            },
            fieldAttr: {
                placeholder: "Set new private key",
            },
        },
        {
            name: "remote_public_key",
            component: "textarea",
            fieldProps: {
                label: "Remote public key",
            },
        },
        {
            name: "last_sync_send_at",
            component: "value",
            fieldProps: {
                label: "Last sync sent",
            },
        },
        {
            name: "last_sync_receive_at",
            component: "value",
            fieldProps: {
                label: "Last sync received",
            },
        },
    ],
    detailsSidebar: [
        {
            label: "Last activity",
            valueKey: "last_activity",
            dto: (value: any) => (value ? <TimeAgoWithDateTimeFormatter datetime={value} /> : null),
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) =>
            normalizeNullableStringFields(resourceData, ["cacert", "remote_public_key"]),
    },
};
export default config;
